import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import { StatusIcon, StatusBadge, EmptyState } from '../ui';
import { RefreshButton } from '../common';

const RequestListTable = ({
  currentRequests,
  requests,
  sortConfig,
  handleSort,
  handleUpdateStatus,
  isRefreshing,
  requestsLoading,
  handleRefresh,
  searchTerm,
  statusFilter
}) => {
  const navigate = useNavigate();

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <ArrowsUpDownIcon className="h-4 w-4 text-gray-400" />;
    }
    
    return sortConfig.direction === 'asc' 
      ? <ChevronLeftIcon className="h-4 w-4 text-indigo-600 rotate-90" />
      : <ChevronRightIcon className="h-4 w-4 text-indigo-600 -rotate-90" />;
  };

  const getFromValue = (request) => {
    if (request?.origin) {
      return 'Web';
    }
    return request.sender_email || 'N/A';
  };

  const getSupplierName = (request) => {
    if (request?.document_types && typeof request.document_types === 'object') {
      // Get the first supplier name from document_types
      const suppliers = Object.values(request.document_types);
      return suppliers.length > 0 ? suppliers[0] : 'N/A';
    }
    return 'N/A';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        {currentRequests.length === 0 ? (
          <EmptyState
            icon={DocumentTextIcon}
            title="No requests found"
            description={
              searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : requests.length === 0 
                  ? 'No data loaded. Click refresh to load requests.'
                  : 'Create your first freight request to get started.'
            }
            action={
              requests.length === 0 && (
                <RefreshButton
                  onClick={handleRefresh}
                  isRefreshing={isRefreshing}
                  loading={requestsLoading}
                  text="Load Data"
                  loadingText="Loading..."
                  size="lg"
                />
              )
            }
            className="p-8 md:p-12"
          />
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  scope="col" 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('request_id')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Request ID</span>
                    {getSortIcon('request_id')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('front_status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {getSortIcon('front_status')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('sender_email')}
                >
                  <div className="flex items-center space-x-1">
                    <span>From</span>
                    {getSortIcon('sender_email')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('supplier')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Supplier</span>
                    <span 
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 cursor-help"
                      title="AI Classified Data"
                    >
                      AI
                    </span>
                    {getSortIcon('supplier')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <div className="flex items-center space-x-1">
                    <span>AWB</span>
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('timestamp')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Created</span>
                    {getSortIcon('timestamp')}
                  </div>
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentRequests.map((request) => {
                const awb = request.courier_awb_number;
                const supplierName = getSupplierName(request);
                return (
                <tr 
                  key={request.id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/requests/${request.request_id}`)}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <StatusIcon status={request.front_status} />
                      <span className="text-sm font-medium text-gray-900">#{request.request_id}</span>
                      {Number(request.attempt_number || 1) > 1 && (
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                          Attempt {request.attempt_number}
                        </span>
                      )}
                      {['queued', 'processing'].includes(request.reprocess_status) && (
                        <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700">
                          Reprocessing
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge status={request.front_status} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 truncate max-w-[200px]">{getFromValue(request)}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 truncate max-w-[180px]" title={supplierName}>
                      {supplierName}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900 truncate max-w-[140px]">
                      {awb || 'N/A'}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {format(new Date(request.timestamp), 'MMM dd, yyyy')}
                    </div>
                    <div className="text-xs text-gray-500">
                      {format(new Date(request.timestamp), 'HH:mm')}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end space-x-2">
                      <select
                        value={request.front_status}
                        onChange={(e) => handleUpdateStatus(request.request_id, e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 hover:border-gray-400 transition-colors"
                      >
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RequestListTable;
